'use client'

import { useState } from 'react'
import { couvertureDirecte } from '@/lib/couverture-drive'
import { cn } from './cn'

/**
 * Une couverture hébergée ailleurs, qui s'efface quand elle échoue.
 *
 * ## ⚠️ Pourquoi ce composant est client, alors que `Couverture` ne l'est pas
 *
 * Pour une seule raison : `onError`. Sans lui, une image qui ne se charge pas
 * laisse le navigateur dessiner **sa propre icône de rupture** — un carré barré,
 * par-dessus la forme de marque qui tenait pourtant la place très bien. Signalé
 * par Baptiste le 2026-09-02, capture d'écran mobile à l'appui, sur une
 * couverture de module servie par Drive.
 *
 * ⚠️ **L'adresse était valide.** Vérifié le même jour : `200`, JPEG 760×428,
 * après une redirection. L'échec est donc intermittent — Drive limite les
 * vignettes servies, et un réseau mobile hésitant suffit. On ne peut pas
 * l'empêcher ; on peut faire qu'il ne se voie pas.
 *
 * ⚠️ **Rien ne remplace l'image**, volontairement : l'aplat de marque est déjà
 * rendu dessous par l'appelant. Y superposer une icône « image manquante »
 * n'apprendrait rien à l'apprenant, qui n'a de toute façon rien à faire de ce
 * problème.
 */
export function ImageDistante({
  url,
  priorite = false,
  className,
}: {
  url: string
  priorite?: boolean
  className?: string
}) {
  const [echoue, setEchoue] = useState(false)
  if (echoue) return null

  /*
   * ⚠️ **On vise le CDN, pas l'aiguillage.** `drive.google.com/thumbnail` ne sert
   * jamais l'image : il redirige, à chaque appel, vers `lh3.googleusercontent.com`
   * — mesuré le 2026-09-02, huit fois sur huit. Chaque couverture coûtait donc un
   * aller-retour de plus sur un point d'entrée limité en débit, et sur une grille
   * qui en charge quatre à la fois, l'une d'elles tombait. 158 couvertures sont
   * dans ce cas en production.
   */
  const source = couvertureDirecte(url)

  return (
    // eslint-disable-next-line @next/next/no-img-element -- hôte inconnu par construction, voir `Couverture`
    <img
      src={source}
      alt=""
      loading={priorite ? 'eager' : 'lazy'}
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setEchoue(true)}
      className={cn('size-full object-cover', className)}
    />
  )
}
